<script setup>
import Layout from "../Layouts/Customer.vue";
import Button from "primevue/button";
import {ref} from "vue";
import {useToast} from "primevue/usetoast";
import moment from "moment/moment";
import {useForm} from "@inertiajs/inertia-vue3";
import {true$} from "../utils/validators";
import {useVuelidate} from "@vuelidate/core";
import {scrollToFirstError} from "../utils/form";
import {dataURLtoFile} from "../utils/file-utils";
import ConsentSignature from "../Components/ConsentSignature.vue";
import {Inertia} from "@inertiajs/inertia";


const props = defineProps([
    'customer',
    'menuItems',
    'flash',
]);
const toast = useToast()
const consent = ref(props.customer?.btl_emsella_consent)
const consentSlug = ref('btl-emsella-consents')
const isEdit = ref(Boolean(consent.value))
const submitting = ref(false)
const consentSignature = ref(null)
const date = ref(
    isEdit.value ? moment(consent.value.updated_at) : moment()
)
const form = useForm({
    signature_key: null,
    customer_uuid: props.customer.uuid,
    is_agree_signature: isEdit.value,
    signature_img: isEdit.value ? consent.value.signature_img : null,
    has_signature: null,
    date: date.value.format('DD.MM.YYYY'),
});

const rules = {
    form: {
        is_agree_signature: {
            true$,
        },
        signature_key: {
            custom$: {
                $validator: function(value) {
                    return form.has_signature !== false
                },
                $message: 'Dieses Feld ist erforderlich',
            }
        }
    }
}

const v$ = useVuelidate(rules, {
    form,
})

const submit = async (e, isDownload) => {
    let signatureData = null
    const signaturePad = consentSignature.value.getSignaturePad()

    if (signaturePad) {
        const { isEmpty, data } = signaturePad.saveSignature()

        form.has_signature = ! isEmpty
        signatureData = data
    }

    v$.value.$validate()

    if (v$.value.$errors.length) {
        scrollToFirstError()
        toast.add({
            severity: "error",
            summary: "Error",
            life: 5000,
        });

        return
    }

    submitting.value = true

    if (signatureData) {
        const { key:signatureKey } = await Vapor.store(dataURLtoFile(signatureData), {
            progress: progress => {
            }
        })

        form.signature_key = signatureKey
    }

    const postUrl = isEdit.value
        ? `/${consentSlug.value}/${consent.value.uuid}`
        : `/${consentSlug.value}`

    form.post(
        postUrl,
        {
            preserveState: false,
            onError: (errors) => {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    life: 5000,
                });
            },

            onSuccess: (page) => {
                toast.add({
                    severity: "success",
                    summary: "Aktualisiert",
                    life: 5000,
                });

                if (isDownload) {
                    window.location.href = `/${consentSlug.value}/${consent.value.uuid}/download`
                }
            },

            onFinish: (visit) => {
                submitting.value = false;
            }
        }
    )
}

const download = async () => {
    if (! submitting.value) {
        await submit(null, true)
    }
}
</script>
<template>
    <Layout :customer="customer" :menu-items="menuItems" :flash="flash">
        <div class="px-14 py-7">
            <div class="relative mb-9">
                <h1 class="text-black text-base font-bold mb-9">
                    Einverständniserklärung und Vertrag für die Emsella-Therapie
                </h1>

                <div v-if="isEdit"
                     class="absolute top-0 right-0">
                    <i class="pi pi-file-pdf text-[#C5C6CC] cursor-pointer" @click="download"></i>
                </div>
            </div>

            <div class="text-sm">
                <h3 class="text-black font-bold">Einführung</h3>

                <div class="flex flex-col gap-3 mb-9">
                    <p>
                        Emsella ist ein nicht-invasives Verfahren zur Behandlung von Harninkontinenz und zur Stärkung der
                        Beckenbodenmuskulatur. Emsella verwendet elektromagnetische Energie, um tiefe Beckenbodenmuskelkontraktionen zu erzeugen.
                    </p>
                </div>

                <ul class="mb-9">
                    <li>
                        <h3 class="text-sm font-bold my-5">
                            1. Vertragsgegenstand
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Der Kunde beauftragt VIGORY® mit der Durchführung von Behandlungen mit dem Emsella zur Stärkung der
                                Beckenbodenmuskulatur.
                            </p>

                            <p>
                                Umfang und Preise der Leistungen sind auf der VIGORY-Website ersichtlich und können unter dem
                                folgenden Link abgerufen werden [Einfügen Link/Verweis]. Es gelten die im Zeitpunkt der Buchung
                                ausgewiesenen Preise.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            2. Behandlungsablauf
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es werden insgesamt 6-8 Sitzungen empfohlen.
                            </p>

                            <p>
                                Für die Behandlung sollte bequeme Kleidung getragen werden ohne metallische Komponenten wie
                                Reissverschlüsse oder Knöpfe. Es darf kein Schmuck getragen werden und die Nutzung von elektronischen
                                Geräten während der Behandlung ist nicht gestattet.
                            </p>

                            <p>
                                Der Patient sitzt vollständig bekleidet auf dem Emsella-Stuhl.
                            </p>

                            <p>
                                Eine Sitzung dauert in der Regel etwa 30 Minuten.
                            </p>

                            <p>
                                Der Patient kann während der Behandlung eine intensive Muskelkontraktion spüren.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            3. Risiken und Nebenwirkungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Wie bei jeder Behandlung gibt es potenzielle Risiken und Nebenwirkungen die auftreten können.
                            </p>

                            <p>
                                Dazu gehören, sind aber nicht beschränkt auf:
                            </p>

                            <p>
                                Vorübergehender Muskelkater im Beckenbodenbereich, leichtes Unwohlsein während der Behandlung
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            4. Alternativen zu Emsella-Behandlungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es gibt verschiedene Alternativen zur Emsella-Therapie, einschliesslich Physiotherapie und
                                Beckenbodentraining, Biofeedback-Therapie sowie operative Eingriffe.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            5. Vorbehalte und Gesundheitsschutz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY behält sich das Recht vor, die Behandlung abzulehnen oder abzubrechen, insbesondere wenn
                                gesundheitliche Bedenken vorliegen.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            6. Leistungsinhalt und Haftung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Es kann bis zu 3 Monate dauern, bis sich die endgültigen Resultate einstellen. In seltenen Fällen kann der
                                Erfolg gering oder ausbleibend sein.
                            </p>

                            <p>
                                Der Leistungsinhalt umfasst die fachgerechte Durchführung der Behandlung.
                            </p>

                            <p>
                                VIGORY garantiert keinen spezifischen Behandlungserfolg.
                            </p>

                            <p>
                                VIGORY haftet gemäss den gesetzlichen Vorschriften für Verletzungen von vertraglichen und
                                ausservertraglichen Pflichten. Schadenersatz wird bei Vorsatz und grober Fahrlässigkeit geleistet. Bei
                                leichter Fahrlässigkeit haftet VIGORY nur bei Verletzung von Leben, Körper oder Gesundheit sowie bei
                                Verletzung wesentlicher Vertragspflichten. In diesen Fällen ist die Haftung auf den vorhersehbaren,
                                typischerweise eintretenden Schaden begrenzt. VIGORY haftet nicht für Schäden, die auf Umstände
                                zurückzuführen sind, für die der Kunde verantwortlich ist.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            7. Terminverschiebungen und -absagen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Vereinbarte Folgetermine sind verbindlich. VIGORY stellt Personal und technische Ausstattung für die
                                Termine bereit. Terminverschiebungen sind kostenfrei, wenn sie mindestens 24 Stunden vor dem
                                vereinbarten Termin erfolgen.
                            </p>

                            <p>
                                Bei Absagen innerhalb von 24 Stunden vor dem Termin oder bei Nichterscheinen kann VIGORY die
                                vereinbarte Vergütung verlangen. Einsparungen oder anderweitige Verwendung der Kapazitäten werden
                                berücksichtigt.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            8. Datenschutz
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                VIGORY erhebt und bearbeitet Personendaten der Kunden nach Massgabe der Datenschutzerklärung,
                                die unter folgendem Link abgerufen werden kann: https://www.vigory.ch/datenschutz
                            </p>

                            <p>
                                Der Kunde stimmt der fotografischen Dokumentation der behandelten Bereiche und der anonymen
                                Veröffentlichung der Ergebnisse zu Werbezwecken zu.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            9. Zustimmung zur Behandlung
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich bestätige hiermit, dass ich die oben genannten Informationen zur Emsella Therapie gelesen und
                                verstanden habe. Mir ist bewusst, dass jeder Mensch individuell reagiert und gewisse Nebeneffekte
                                auftreten können und dass das Behandlungsergebnis negativ beeinträchtigt werden kann, wenn das
                                definierte Therapiekonzept nicht zu Ende geführt wird. Ich kenne die Risiken und möglichen
                                Nebenwirkungen und willige freiwillig und nach ausreichender Überlegung in die Durchführung der
                                Behandlungen ein. Ich hatte die Gelegenheit, Fragen zu stellen, und diese wurden zu meiner
                                Zufriedenheit beantwortet.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            10. Haftungsausschluss
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Ich verstehe, dass das Ergebnis der Behandlung variieren kann und keine spezifischen Resultate
                                garantiert werden können. Ich entbinde hiermit die behandelnde Fachkraft, sowie das Center von
                                jeglicher Haftung, soweit gesetzlich zulässig.
                            </p>
                        </div>
                    </li>

                    <li>
                        <h3 class="text-sm font-bold my-5">
                            11. Schlussbestimmungen
                        </h3>

                        <div class="flex flex-col gap-3">
                            <p>
                                Die Angaben im Anamnesefragebogen habe ich wahrheitsgemäss beantwortet.
                                Wir bitten Sie, uns umgehend über jegliche Änderungen Ihres Gesundheitszustandes zu informieren.
                                Dies umfasst, ist aber nicht beschränkt auf, neu diagnostizierte Erkrankungen, Änderungen bestehender
                                Krankheiten, die Einnahme neuer Medikamente oder Änderungen in der Medikation. Diese
                                Informationen sind entscheidend, um Ihre Behandlung optimal anzupassen. Bitte beachten Sie, dass wir
                                keinerlei Verantwortung für gesundheitliche Folgen übernehmen können, die auf nicht mitgeteilte
                                Änderungen zurückzuführen sind.
                            </p>

                            <p>
                                Vielen Dank für Ihr Verständnis und Ihre Kooperation.
                            </p>
                        </div>
                    </li>
                </ul>

                <div class="mb-9">
                    <ConsentSignature
                        ref="consentSignature"
                        :customer="customer"
                        :form="form"
                        :v$="v$"
                        :is-edit="isEdit"
                    />
                </div>

                <div class="flex justify-end">
                    <Button
                        @click="submit"
                        :loading="submitting"
                        label="speichern"
                        class="min-w-[100px] !bg-black !text-[#FFF053] !border-none"
                    />
                </div>
            </div>
        </div>
    </Layout>
</template>
